/**
 * Arcade high-score UI — the table renderer and the initials selector
 * (LOCKED-DESIGN 18.28). Shell-owned DOM, deliberately OUTSIDE the game HUD:
 * nothing here carries `data-hud-element` (the browser QA readability sweep
 * queries the whole document for that attribute, and a results overlay would
 * fail its safe-area rules by construction — the shell pause button precedent
 * notwithstanding, results chrome stays out of the HUD contract).
 *
 * INPUT: the shell has no gamepad plumbing and the game's InputManager is
 * paused while any overlay is up, so the selector runs its own
 * `navigator.getGamepads()` poll with edge detection (standard mapping:
 * dpad 12-15, A=0 confirm, B=1 back), alongside direct keyboard typing.
 * The poll exists only while the selector is mounted.
 *
 * MOTION: no blinking, no marquee, no flash — the celebratory state is a
 * static banner, so the flash-reduction policy is satisfied by construction
 * rather than by gating.
 */
import { INITIALS_ALPHABET, INITIALS_MAX, TABLE_DISPLAY_SIZE, type HighScoreEntry, sanitizeInitials } from "@host/HighScoreTable.js";
import type { LedgerSource } from "@host/arcade.js";

/** Render a top-N table into `container`. `highlight` marks the fresh entry. */
export function renderScoreTable(
  container: HTMLElement,
  entries: readonly HighScoreEntry[],
  options: { source: LedgerSource; simple: boolean; highlight?: HighScoreEntry | null; heading?: string; limit?: number },
): void {
  const limit = options.limit ?? TABLE_DISPLAY_SIZE;
  container.replaceChildren();
  container.classList.toggle("is-simple", options.simple);

  const heading = document.createElement("p");
  heading.className = "score-table-heading";
  heading.textContent = options.heading ?? (options.simple ? "HIGH SCORES!" : "HIGH SCORES");
  container.append(heading);

  if (entries.length === 0) {
    const empty = document.createElement("p");
    empty.className = "score-table-empty";
    empty.textContent = options.simple ? "NO SCORES YET — BE FIRST!" : "No scores yet. Set the first one.";
    container.append(empty);
  } else {
    const list = document.createElement("ol");
    list.className = "score-table";
    list.dataset["testid"] = "score-table";
    entries.slice(0, limit).forEach((entry, index) => {
      const row = document.createElement("li");
      const you = options.highlight
        && entry.initials === options.highlight.initials
        && entry.score === options.highlight.score
        && entry.ts === options.highlight.ts;
      row.className = you ? "you" : "";
      const rank = document.createElement("span");
      rank.className = "rank";
      rank.textContent = String(index + 1);
      const initials = document.createElement("span");
      initials.className = "initials";
      initials.textContent = entry.initials.padEnd(INITIALS_MAX, "·");
      const score = document.createElement("span");
      score.className = "pts";
      score.textContent = entry.score.toLocaleString("en-US");
      row.append(rank, initials, score);
      list.append(row);
    });
    container.append(list);
  }

  // Which cabinet is this? Family board (shared backend) vs this machine only.
  const source = document.createElement("p");
  source.className = "score-table-source";
  source.textContent = options.source === "shared"
    ? (options.simple ? "FAMILY BOARD" : "Family board · shared")
    : (options.simple ? "THIS MACHINE" : "This machine only");
  container.append(source);
}

export interface InitialsEntryHandle {
  element: HTMLElement;
  /** Stop the gamepad poll and key listener. Idempotent; called on any teardown. */
  dispose(): void;
}

/**
 * The three-slot arcade initials selector. Confirm calls `onDone(initials)`
 * exactly once; Skip calls `onSkip()` exactly once. The widget does not
 * unmount itself — the caller owns DOM placement and teardown.
 */
export function createInitialsEntry(options: {
  initials: string;
  simple: boolean;
  onDone(initials: string): void;
  onSkip(): void;
}): InitialsEntryHandle {
  const letters = sanitizeInitials(options.initials).padEnd(INITIALS_MAX, "A").split("");
  let slot = 0;
  let settled = false;

  const root = document.createElement("div");
  root.className = `initials-entry${options.simple ? " is-simple" : ""}`;
  root.dataset["testid"] = "initials-entry";

  const banner = document.createElement("p");
  banner.className = "initials-banner";
  banner.textContent = options.simple ? "YOU MADE THE BOARD!" : "HIGH SCORE — ENTER YOUR INITIALS";
  const hint = document.createElement("p");
  hint.className = "initials-hint";
  hint.textContent = options.simple ? "PICK YOUR LETTERS!" : "Type, arrows, or D-pad · Enter/Ⓐ to lock in";

  const slotsRow = document.createElement("div");
  slotsRow.className = "initials-slots";
  const slotEls: { up: HTMLButtonElement; letter: HTMLElement; down: HTMLButtonElement }[] = [];
  for (let i = 0; i < INITIALS_MAX; i++) {
    const cell = document.createElement("div");
    cell.className = "initials-slot";
    const up = document.createElement("button");
    up.type = "button";
    up.className = "slot-arrow";
    up.textContent = "▲";
    up.setAttribute("aria-label", `letter ${i + 1} up`);
    const letter = document.createElement("span");
    letter.className = "slot-letter";
    letter.dataset["testid"] = `initials-slot-${i}`;
    const down = document.createElement("button");
    down.type = "button";
    down.className = "slot-arrow";
    down.textContent = "▼";
    down.setAttribute("aria-label", `letter ${i + 1} down`);
    cell.append(up, letter, down);
    slotsRow.append(cell);
    slotEls.push({ up, letter, down });
    up.addEventListener("click", () => { slot = i; cycle(-1); });
    down.addEventListener("click", () => { slot = i; cycle(1); });
  }

  const buttons = document.createElement("div");
  buttons.className = "initials-buttons";
  const ok = document.createElement("button");
  ok.type = "button";
  ok.className = "primary initials-ok";
  ok.dataset["testid"] = "initials-ok";
  ok.textContent = options.simple ? "DONE!" : "Lock in ▸";
  ok.setAttribute("data-autofocus", "");
  const skip = document.createElement("button");
  skip.type = "button";
  skip.className = "linkish";
  skip.dataset["testid"] = "initials-skip";
  skip.textContent = options.simple ? "skip" : "Skip";
  buttons.append(skip, ok);

  root.append(banner, hint, slotsRow, buttons);

  function paint(): void {
    slotEls.forEach((cell, i) => {
      cell.letter.textContent = letters[i]!;
      cell.letter.classList.toggle("active", i === slot);
    });
  }

  function cycle(direction: -1 | 1): void {
    const index = INITIALS_ALPHABET.indexOf(letters[slot]!);
    const next = (index + direction + INITIALS_ALPHABET.length) % INITIALS_ALPHABET.length;
    letters[slot] = INITIALS_ALPHABET[next]!;
    paint();
  }

  function move(direction: -1 | 1): void {
    slot = Math.max(0, Math.min(INITIALS_MAX - 1, slot + direction));
    paint();
  }

  function settle(kind: "done" | "skip"): void {
    if (settled) return;
    settled = true;
    dispose();
    if (kind === "done") options.onDone(sanitizeInitials(letters.join("")));
    else options.onSkip();
  }

  ok.addEventListener("click", () => settle("done"));
  skip.addEventListener("click", () => settle("skip"));

  function onKey(event: KeyboardEvent): void {
    if (settled) return;
    // Typing into any real form field (the Standard results drawer has a
    // playtest-notes textarea) must never be captured as initials input
    // (review finding).
    const target = event.target;
    if (target instanceof HTMLInputElement || target instanceof HTMLTextAreaElement || target instanceof HTMLSelectElement) return;
    const char = event.key.length === 1 ? event.key.toUpperCase() : "";
    if (char && INITIALS_ALPHABET.includes(char)) {
      letters[slot] = char;
      if (slot < INITIALS_MAX - 1) slot++;
      paint();
      event.preventDefault();
      return;
    }
    switch (event.key) {
      case "ArrowUp": cycle(-1); event.preventDefault(); break;
      case "ArrowDown": cycle(1); event.preventDefault(); break;
      case "ArrowLeft": move(-1); event.preventDefault(); break;
      case "ArrowRight": move(1); event.preventDefault(); break;
      case "Backspace": move(-1); event.preventDefault(); break;
      case "Enter": settle("done"); event.preventDefault(); break;
    }
  }
  window.addEventListener("keydown", onKey);

  // Gamepad poll — standard mapping, edge-detected, alive only while mounted.
  let rafId = 0;
  let prevButtons: boolean[] = [];
  let padPrimed = false;
  function pollPad(): void {
    if (settled) return;
    const pad = typeof navigator !== "undefined" && navigator.getGamepads
      ? Array.from(navigator.getGamepads()).find((candidate) => candidate?.connected)
      : null;
    if (pad) {
      const pressed = pad.buttons.map((button) => button.pressed);
      // The first sample only RECORDS: a button still held from gameplay (the
      // A the player was mashing at the buzzer) must not read as a fresh press
      // and instantly confirm the initials (review finding).
      if (!padPrimed) {
        padPrimed = true;
        prevButtons = pressed;
        rafId = requestAnimationFrame(pollPad);
        return;
      }
      const edge = (i: number) => pressed[i] === true && prevButtons[i] !== true;
      if (edge(12)) cycle(-1);      // dpad up
      if (edge(13)) cycle(1);       // dpad down
      if (edge(14)) move(-1);       // dpad left
      if (edge(15)) move(1);        // dpad right
      if (edge(0)) {                // A: advance slots, confirm on the last
        if (slot < INITIALS_MAX - 1) move(1);
        else { settle("done"); return; }
      }
      if (edge(1)) move(-1);        // B: back a slot
      prevButtons = pressed;
    }
    rafId = requestAnimationFrame(pollPad);
  }
  rafId = requestAnimationFrame(pollPad);

  function dispose(): void {
    window.removeEventListener("keydown", onKey);
    cancelAnimationFrame(rafId);
  }

  paint();
  return { element: root, dispose };
}
